<template>
  <div class="flex h-screen bg-background font-outfit overflow-hidden transition-colors duration-300">
    <a href="#main-content" class="skip-link">Passer au contenu principal</a>
    <!-- Sidebar -->
    <aside class="w-72 bg-zinc-950 text-zinc-300 flex flex-col border-r border-zinc-800/50 shadow-2xl z-20 transition-all duration-300">
      <div class="p-6 flex items-center gap-3 border-b border-zinc-800/50 group cursor-pointer" @click="router.push({ name: 'finance_home' })">
        <div class="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-white transition-all duration-300 overflow-hidden">
          <img :src="logoUrl" alt="Logo" class="w-7 h-7 object-contain" />
        </div>
        <div class="flex flex-col">
          <span class="text-white font-black tracking-tighter text-lg leading-none">CODIFYO</span>
          <span class="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mt-1">Finance Hub</span>
        </div>
      </div>

      <nav class="flex-1 py-6 px-4 space-y-8 overflow-y-auto custom-scrollbar">
        <!-- Main Nav -->
        <div class="space-y-1">
          <h3 class="px-4 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 mb-4">Menu Principal</h3>
          
          <router-link :to="{ name: 'finance_pending_movements' }" 
             class="flex items-center justify-between px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 hover:bg-white/5 active:scale-95 group mb-1"
             :class="route.name === 'finance_pending_movements' ? 'bg-indigo-500/10 text-white shadow-sm' : 'text-zinc-400'"
          >
            <div class="flex items-center gap-3">
              <CheckSquare class="w-4 h-4" :class="route.name === 'finance_pending_movements' ? 'text-indigo-400' : 'text-zinc-500'" />
              <span>Actions requises</span>
            </div>
            <span v-if="pendingCount > 0" class="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg shadow-red-500/20">
              {{ pendingCount }}
            </span>
          </router-link>

          <div class="space-y-1">
            <button 
              class="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 hover:bg-white/5 active:scale-95 group focus-visible:ring-2 focus-visible:ring-indigo-500"
              :class="isInvestmentsOpen ? 'text-white' : 'text-zinc-400'"
              @click="isInvestmentsOpen = !isInvestmentsOpen"
              aria-haspopup="true"
              :aria-expanded="isInvestmentsOpen"
              aria-controls="investments-menu"
            >
              <div class="flex items-center gap-3">
                <LayoutDashboard class="w-4 h-4" :class="isInvestmentsOpen ? 'text-indigo-400' : 'text-zinc-500'" />
                Investissements
              </div>
              <ChevronDown class="w-3.5 h-3.5 transition-transform duration-300" :class="{ 'rotate-180': isInvestmentsOpen }" />
            </button>
            
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <ul v-if="isInvestmentsOpen" id="investments-menu" class="mt-1 ml-4 pl-4 border-l border-zinc-800 space-y-1">
                <template v-if="mainCategories.length > 0">
                  <li>
                    <router-link 
                      :to="{ name: 'finance_home' }" 
                      class="block px-4 py-2 rounded-md text-xs font-bold transition-all"
                      :class="route.name === 'finance_home' ? 'text-white bg-white/5' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'"
                    >Tout voir</router-link>
                  </li>
                  <li v-for="cat in mainCategories" :key="cat.id">
                    <router-link 
                      :to="{ name: 'finance_category', params: { id: cat.id, name: slugify(cat.name) } }"
                      class="block px-4 py-2 rounded-md text-xs font-bold transition-all"
                      :class="route.params.id == cat.id ? 'text-white bg-white/5' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'"
                    >{{ cat.name }}</router-link>
                  </li>
                </template>
                <template v-else>
                  <li v-for="i in 3" :key="i" class="px-4 py-2">
                    <Skeleton class="h-3 w-24 bg-zinc-800" />
                  </li>
                </template>
              </ul>
            </transition>
          </div>

          <router-link :to="{ name: 'finance_budget' }" 
             class="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 hover:bg-white/5 active:scale-95 group text-zinc-400"
             :class="route.name === 'finance_budget' ? 'bg-indigo-500/10 text-white' : ''"
          >
            <BarChart3 class="w-4 h-4 text-zinc-500 group-hover:text-indigo-400 transition-colors" />
            Budget Prévisionnel
          </router-link>

          <router-link :to="{ name: 'finance_subscriptions' }" 
             class="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 hover:bg-white/5 active:scale-95 group text-zinc-400"
             :class="route.name === 'finance_subscriptions' ? 'bg-indigo-500/10 text-white' : ''"
          >
            <CalendarDays class="w-4 h-4 text-zinc-500 group-hover:text-indigo-400 transition-colors" />
            Mes Abonnements
          </router-link>
        </div>

        <!-- Settings Section -->
        <div class="space-y-1">
          <h3 class="px-4 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-600 mb-4">Configuration</h3>
          <router-link :to="{ name: 'finance_settings' }" 
             class="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-all duration-200 hover:bg-white/5 active:scale-95 group text-zinc-400"
             :class="route.name === 'finance_settings' ? 'bg-indigo-500/10 text-white' : ''"
          >
            <Settings class="w-4 h-4 text-zinc-500 group-hover:text-indigo-400 transition-colors" />
            Paramètres
          </router-link>
        </div>
      </nav>

      <!-- Bottom Profile -->
      <div class="p-4 border-t border-zinc-800 bg-zinc-950/50">
        <div class="flex items-center gap-3 p-2 rounded-xl mb-4">
          <div v-if="userData" class="w-9 h-9 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-white font-black text-xs shadow-lg">
            {{ userDisplayName.charAt(0).toUpperCase() }}
          </div>
          <Skeleton v-else class="w-9 h-9 rounded-full bg-zinc-800" />
          <div class="flex flex-col min-w-0">
             <template v-if="userData">
               <span class="text-xs font-bold text-white truncate">{{ userDisplayName }}</span>
               <span v-if="userData?.firstName" class="text-[10px] text-zinc-500 font-medium truncate">{{ userData.email }}</span>
               <span v-else class="text-[10px] text-zinc-500 font-medium">Investisseur Gold</span>
             </template>
             <template v-else>
               <Skeleton class="h-3 w-20 mb-1 bg-zinc-800" />
               <Skeleton class="h-2 w-28 bg-zinc-800" />
             </template>
          </div>
        </div>
        <button 
          class="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold text-zinc-400 hover:text-white hover:bg-red-500/10 transition-all border border-zinc-800 hover:border-red-500/20"
          @click="logout"
        >
          <LogOut class="w-3.5 h-3.5" /> Déconnexion
        </button>
      </div>
    </aside>

    <!-- Main Content -->
    <main id="main-content" class="flex-1 flex flex-col min-w-0 bg-background relative transition-colors duration-300 outline-none" tabindex="-1">
      <!-- Top header -->
      <header class="h-16 bg-card border-b border-border px-8 flex items-center justify-between z-10 shadow-sm transition-colors">
        <h1 class="text-lg font-black text-foreground tracking-tight flex items-center gap-2">
          <div class="w-1 h-4 bg-primary rounded-full" aria-hidden="true"></div>
          {{ pageTitle }}
        </h1>
 
        <div class="flex items-center gap-4">
          <div class="flex items-center bg-muted p-1 rounded-lg border border-border">
            <button 
              @click="toggleDarkMode" 
              class="flex items-center justify-center w-9 h-8 rounded-md transition-all duration-200 active:scale-90 focus-visible:ring-2 focus-visible:ring-primary"
              :class="isDarkMode ? 'bg-background shadow-sm text-yellow-500' : 'text-zinc-400 hover:text-zinc-600'"
              :title="isDarkMode ? 'Passer en mode clair' : 'Passer en mode sombre'"
              :aria-label="isDarkMode ? 'Passer en mode clair' : 'Passer en mode sombre'"
            >
              <Sun v-if="isDarkMode" class="w-4 h-4" />
              <Moon v-else class="w-4 h-4" />
            </button>
            <div class="w-px h-4 bg-border mx-1" aria-hidden="true"></div>
            <button 
              @click="toggleAmounts" 
              class="flex items-center justify-center w-9 h-8 rounded-md transition-all duration-200 active:scale-90 focus-visible:ring-2 focus-visible:ring-primary"
              :class="!isAmountsHidden ? 'bg-background shadow-sm text-foreground' : 'text-zinc-400 hover:text-zinc-600'"
              :title="isAmountsHidden ? 'Afficher les montants' : 'Masquer les montants'"
              :aria-label="isAmountsHidden ? 'Afficher les montants' : 'Masquer les montants'"
            >
              <Eye v-if="!isAmountsHidden" class="w-4 h-4" />
              <EyeOff v-else class="w-4 h-4" />
            </button>
          </div>
          
          <div class="h-8 w-px bg-border mx-2"></div>

          <div class="flex items-center gap-3">
             <div class="flex flex-col items-end hidden sm:flex">
               <span class="text-xs font-bold text-foreground">{{ userDisplayName }}</span>
               <div v-if="pendingCount > 0" class="flex items-center gap-1 text-[9px] text-red-500 font-black uppercase tracking-tighter">
                  <div class="w-1 h-1 bg-red-500 rounded-full animate-pulse"></div>
                  {{ pendingCount }} prévisions à valider
               </div>
             </div>
          </div>
        </div>
      </header>

      <!-- Page view -->
      <div class="flex-1 overflow-y-auto custom-scrollbar">
        <router-view v-slot="{ Component }">
          <transition 
            name="fade" 
            mode="out-in"
          >
            <component :is="Component" />
          </transition>
        </router-view>
      </div>
    </main>
  </div>
  <ToastNotification />
</template>

<script setup>
import { ref, onMounted, onUnmounted, provide, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { 
  LayoutDashboard, ChevronDown, CheckSquare, PlusCircle, 
  Settings, LogOut, Eye, EyeOff, Sun, Moon, BarChart3, CalendarDays
} from 'lucide-vue-next'
import logoUrl from '../../img/favicon-finance.png'
import ToastNotification from './ToastNotification.vue'
import { Skeleton } from '@/components/ui/skeleton'
import { useGlobalState } from '../../composables/useGlobalState.js'
import { slugify } from '@/lib/utils'

const router = useRouter()
const route = useRoute()

const { 
  isAmountsHidden, isDarkMode, toggleAmounts, toggleDarkMode, userData, userDisplayName, 
  pendingCount, mainCategories, fetchUser, fetchPendingCount, fetchCategories 
} = useGlobalState()

const isInvestmentsOpen = ref(false)

const pageTitle = computed(() => {
    if (route.name === 'finance_home' || route.name === 'finance_category') return 'Mes Placements'
    if (route.name === 'finance_pending_movements') return 'Actions requises'
    if (route.name === 'finance_subscriptions') return 'Mes Abonnements'
    if (route.name === 'finance_investment_new') return 'Nouveau Placement'
    if (route.name === 'finance_investment_detail') return 'Détail du Placement'
    if (route.name === 'finance_settings') return 'Paramètres'
    if (route.name === 'finance_budget') return 'Budget Prévisionnel'
    return 'Tableau de bord'
})

onMounted(() => {
    fetchUser()
    fetchPendingCount()
    fetchCategories()
    window.addEventListener('finance-pending-updated', fetchPendingCount)
    window.addEventListener('finance-user-updated', fetchUser)
})

onUnmounted(() => {
    window.removeEventListener('finance-pending-updated', fetchPendingCount)
    window.removeEventListener('finance-user-updated', fetchUser)
})

const logout = () => {
    localStorage.removeItem('apiToken')
    router.push({ name: 'finance_login' })
}
</script>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar for sidebar */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  @apply bg-zinc-800 rounded-full;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  @apply bg-zinc-700;
}
</style>

<style scoped>
/* Scoped overrides if needed */
</style>
